import { IonIcon } from '@ionic/react';
import { notificationsOutline, informationCircleOutline, checkmarkCircleOutline, warningOutline, alertCircleOutline, megaphoneOutline } from 'ionicons/icons';
import { Notification, formatNotificationTime } from '../../utils/notificationData';

interface DropdownPosition {
  top: number;
  right: number;
}

interface NotificationsDropdownProps {
  isOpen: boolean;
  notifications: Notification[];
  unreadCount: number;
  position: DropdownPosition | null;
  onNotificationClick: (notification: Notification) => void;
  onMarkAllRead: () => void;
  onClearAll: () => void;
}

export function NotificationsDropdown({
  isOpen,
  notifications,
  unreadCount,
  position,
  onNotificationClick,
  onMarkAllRead,
  onClearAll,
}: NotificationsDropdownProps) {
  if (!isOpen || !position) return null;

  const getTypeIcon = (type: Notification['type']) => {
    switch (type) {
      case 'info':
        return informationCircleOutline;
      case 'success':
        return checkmarkCircleOutline;
      case 'warning':
        return warningOutline;
      case 'alert':
        return alertCircleOutline;
      default:
        return megaphoneOutline;
    }
  };

  return (
    <div
        className="fixed w-80 flex flex-col z-[101] max-h-96 overflow-hidden border rounded-lg shadow-lg"
        style={{
          backgroundColor: 'var(--ion-background-color)',
          top: position.top - 12,
          right: position.right + 2,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-medium">Notifications</h3>
          {notifications.length > 0 && (
            <div className="flex gap-4">
              {unreadCount > 0 && (
                <button
                  onClick={onMarkAllRead}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={onClearAll}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <IonIcon icon={notificationsOutline} className="mx-auto text-4xl mb-2" />
              <p>No notifications</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <button
                key={notification.id}
                onClick={() => onNotificationClick(notification)}
                className="w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-gray-50"
              >
                <div className="flex items-start gap-3">
                  {/* Type Icon */}
                  <IonIcon icon={getTypeIcon(notification.type)} className="flex-shrink-0 text-xl mt-0.5" />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className={`text-sm ${!notification.read ? 'font-semibold' : 'font-medium'}`}>
                        {notification.title}
                      </h4>
                      {!notification.read && (
                        <span className="w-2 h-2 flex-shrink-0 bg-blue-500 rounded-full mt-1.5"></span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{notification.message}</p>
                    <span className="text-xs text-gray-400">
                      {formatNotificationTime(notification.timestamp)}
                    </span>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
    </div>
  );
}
